import { ref, watch, type Ref } from "vue";

type ScrollTarget = HTMLElement | Window;

let parentEl: ScrollTarget = window;
let scrollOffset = 0;
let topOffset = 0;
let offsetProp = "pageYOffset";
let autoHide: any;
let host: HTMLElement;
const body = document.body;
export let routeName: Ref<string> = ref("");
let navTop = 0;
let navHeight = 0;
let lastAppliedTop = Number.NaN;

let applyNavTop = (value: number) => {
    if (value === lastAppliedTop) {
        return;
    }

    lastAppliedTop = value;
    body.style.setProperty("--nav-top", `${value}px`);
};

let getScrollOffset = () => {
    if (offsetProp === "scrollTop") {
        return Math.max(0, (parentEl as HTMLElement).scrollTop);
    }

    return Math.max(0, window.pageYOffset);
};

watch(routeName, (nv) => {
    if (nv === "home") {
        navTop = 0;
        body.style.setProperty("--nav-position", "fixed");
    } else {
        navTop = 0;
        body.style.setProperty("--nav-position", "sticky"); // apply css variable
    }

    topOffset = navTop;
    applyNavTop(navTop);
});

let prep = () => {
    if (autoHide === undefined) {
        // no given attribute
        return;
    }

    if (!autoHide) {
        // attribute exists but empty value. set to default.
        autoHide = 3;
    }

    if (autoHide) {
        if (typeof autoHide === "string") {
            // html attributes are string
            try {
                autoHide = JSON.parse(autoHide);
            } catch (err) {
                autoHide = 0;
            }
        }

        if (typeof autoHide === "boolean") {
            // if 'true' | 'false' is given.
            autoHide = autoHide ? 3 : 0;
        } else if (typeof autoHide === "number") {
            if (autoHide < 0) {
                // if value is less than 0
                autoHide = 0;
            }
        } else {
            // other types are not allowed
            autoHide = 0;
        }
        if (autoHide) {
            load();
        }
    }
};

let ticking = false;
let calcNavbarPosition = () => {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(() => {
        const scrollOffsetCond = getScrollOffset();

        if (scrollOffsetCond === scrollOffset) {
            ticking = false;
            return;
        }

        const offsetDifference = (scrollOffset - scrollOffsetCond) / autoHide;

        const topOffsetRes = (() => {
            let topOffsetCalc = topOffset + offsetDifference;
            if (topOffsetCalc < -navHeight) {
                // if offset is beyond navHeight
                return -navHeight;
            }
            if (topOffsetCalc > navTop) {
                // offset is positive
                return navTop;
            }

            return topOffsetCalc; // return int range
        })();

        scrollOffset = scrollOffsetCond; // update scroll offset
        topOffset = topOffsetRes;
        applyNavTop(topOffsetRes);
        ticking = false;
    });
};

export let removeListener = (el: ScrollTarget | null) => {
    if (el) {
        el.removeEventListener("scroll", calcNavbarPosition);
    }
};

let isLoaded: ScrollTarget | null = null;

let load = () => {
    removeListener(isLoaded);
    isLoaded = null;

    navHeight = parseInt(window.getComputedStyle(host).height) || host.offsetHeight;

    if (autoHide) {
        let seekScrollableParent = (el: HTMLElement | null): HTMLElement | null => {
            if (el) {
                if (
                    el.scrollHeight > el.clientHeight &&
                    "hidden" !== window.getComputedStyle(el).overflowY
                ) {
                    return el;
                } else {
                    return seekScrollableParent(el.parentElement);
                }
            } else {
                return el;
            }
        };

        let scrollableParent = seekScrollableParent(host.parentElement);
        if (
            scrollableParent &&
            scrollableParent.tagName.toLowerCase() !== "html"
        ) {
            offsetProp = "scrollTop";
            parentEl = scrollableParent;
        } else {
            offsetProp = "pageYOffset";
            parentEl = window;
        }

        parentEl.addEventListener("scroll", calcNavbarPosition, {
            passive: true,
        });

        scrollOffset = getScrollOffset();
        topOffset = navTop;
        applyNavTop(navTop);

        isLoaded = parentEl;
    }
};

export let setAutoHide = (el: HTMLElement, value: any) => {
    /*
      Note:
        - additional attribute "auto-hide" hides nav element when parent is scrolled.
        "auto-hide" attribute can also have number value(greater than 0) for hiding movement curves.
        ex) auto-hide="8" gives slower hiding, auto-hide='2' gives faster hiding.
            when value is not gives default value is '3'
    */
    host = el;
    autoHide = value;
    prep();
};
